import type { FC, JSX } from "react";
import { useCallback, useState } from "react";

import type { ProWebsiteFeaturesLobsDtoLobSegmentData } from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, DotsLoader, TabPanel, Tabs as TabsView } from "@similarweb/ui-components";

import { isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { ScrollAreaWrap } from "pro-features/components/AutocompleteWebCategories/utils";
import { ApplySelection } from "pro-features/components/AutocompleteWithTabs/ApplySelection";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import { translate } from "app/filters/i18nFilter";
import { hasSegmentsClaim, hasCustomLobsSegmentsClaim } from "app/pages/segments/config/segmentsConfigHelpers";
import LobListItems from "app/pages/segments/querybar/components/lob-list-items/LobListItems";
import { openDefaultHooksModal } from "app/services/ModalService";
import { createLinesOfBusinessSegmentId } from "app/services/segments/LinesOfBusinessUtils";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";

import type { IListItem } from "./handlers";
import { NoSegments } from "./NoSegments";
import { Section } from "./Section";
import { Icon, DotsLoaderContainer, CustomTab, CustomTabList } from "./styled";
import {
    ESelectionType,
    type IAsset,
    type ISelectionTypeProps,
    type ITabsProps,
    type IWebsiteResult,
    type SelectedItemsView,
    type SelectionTypes,
} from "./types";

const { Website, Segment } = EAssetType;
const DROPDOWN_AMOUNT_TO_DISPLAY = 100;

const getTabList = (listItems: IAsset[], trackerAssetType: EAssetType) => {
    return listItems.filter(({ type }) => type === trackerAssetType);
};

interface ITabs extends ITabsProps {
    listItems: IAsset[];
    createListItem: (item: IListItem, index: number) => JSX.Element;
    hasQuery: boolean;
    segmentsLoading: boolean;
    hasSegments: boolean;
    selectedMainAsset?: IAsset;
    websiteResults: IWebsiteResult[];
    disableSegments?: boolean;
    selectedItemsView: SelectedItemsView;
    selectionTypes: SelectionTypes;
    onApplyMultiSelect: VoidFunction;
    onBuildSegmentClick?: VoidFunction;
    CustomSegmentsSectionHeader?: FC;
    showLinesOfBusiness?: boolean;
    query?: string;
    lobQuery?: string;
    onItemClick: (item: IListItem) => void;
    onItemClickWithMultiselect: (item: IListItem) => void;
    excludeItems?: IAsset[];
    selectionTypeProps?: ISelectionTypeProps;
    isDisabledListItemSupported?: boolean;
}

export const Tabs: FC<ITabs> = ({
    listItems,
    createListItem,
    hasQuery,
    segmentsLoading,
    hasSegments,
    selectedMainAsset,
    websiteResults,
    showCustomTabsList = true,
    showAccountSegments = true,
    disableSegments,
    disabledTabText,
    scrollMaxHeight = 250,
    selectedItemsView,
    selectionTypes,
    onApplyMultiSelect,
    onBuildSegmentClick,
    CustomSegmentsSectionHeader,
    showLinesOfBusiness,
    query = "",
    lobQuery = "",
    onItemClick,
    onItemClickWithMultiselect,
    excludeItems,
    selectionTypeProps,
    isDisabledListItemSupported,
}) => {
    const isWebsitesMultiSelect = selectionTypes[EAssetType.Website] === ESelectionType.MULTIPLE;
    const isSegmentsMultiSelect = selectionTypes[EAssetType.Segment] === ESelectionType.MULTIPLE;
    const isPredefinedMultiSelect = selectionTypes[EAssetType.Company] === ESelectionType.MULTIPLE;
    const SCROLL_STYLE = { maxHeight: scrollMaxHeight, minHeight: 0 };
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const onTabSelected = (index: number) => {
        setSelectedIndex(index);
    };
    const segmentsTabList = getTabList(listItems, Segment);
    const websitesTabList = getTabList(listItems, Website);
    const mySegments = segmentsTabList.filter(({ isAccount }) => !isAccount);
    const accountSegments = segmentsTabList.filter(({ isAccount }) => isAccount);
    const isSegmentsLocked = isLocked(swSettings.components.CustomSegments);
    const isLinesOfBusinessSupported = hasCustomLobsSegmentsClaim();
    const getPredefinedExcludedItems = (): string[] => {
        return excludeItems?.map((item) => item.id) ?? [];
    };

    const getExcludedDomains = (): string[] => {
        return excludeItems?.filter((item) => item.type === Website).map((item) => item.id) ?? [];
    };

    const effectiveLobQuery = lobQuery || query;

    const getSelectedIndex = useCallback(() => {
        if (!hasSegmentsClaim()) {
            return 0;
        }
        return selectedIndex;
    }, [disableSegments, selectedIndex]);

    const handleLobItemClick = (item: ProWebsiteFeaturesLobsDtoLobSegmentData) => {
        const { Site, LobName, Lob, Favicon } = item;

        const preparedItem = {
            displayText: Site,
            name: LobName,
            image: Favicon || null,
            // @ts-ignore FIXME: strict migration - Argument 'string | null' not assignable to 'string' — LOB API type (ProWebsiteFeaturesLobsDtoLobSegmentData).Site is nullable, id builder requires string
            id: createLinesOfBusinessSegmentId(Site, Lob),
            type: Segment,
        };
        // @ts-ignore FIXME: strict migration - preparedItem not assignable to 'IListItem' — LOB API fields (Site/LobName/Favicon) are nullable but IAsset requires non-null strings
        return isPredefinedMultiSelect ? onItemClickWithMultiselect(preparedItem) : onItemClick(preparedItem);
    };
    return (
        <div className={listItems.length > 0 ? "ListItemsContainer" : ""}>
            {listItems.length > 0 ? (
                <TabsView
                    selectedIndex={getSelectedIndex()}
                    onSelect={onTabSelected}
                >
                    {showCustomTabsList ? (
                        <CustomTabList>
                            <CustomTab>{translate("si.common.websites")}</CustomTab>
                            <CustomTab
                                tooltipText={disableSegments ? disabledTabText : null}
                                // @ts-ignore FIXME: strict migration - No overload matches — Tab 'disabled' prop is typed 'boolean | undefined' but legacy code passes 'boolean | null'
                                disabled={disableSegments || segmentsLoading ? hasSegmentsClaim() : null}
                                onClick={() => {
                                    if (!hasSegmentsClaim()) {
                                        setSelectedIndex(0); // return to default tab view (websites)
                                        openDefaultHooksModal("Segments", LocationService.getCurrentLocationTracking());
                                    }
                                }}
                            >
                                {segmentsLoading && hasSegmentsClaim() ? (
                                    <DotsLoaderContainer>
                                        <DotsLoader />
                                    </DotsLoaderContainer>
                                ) : (
                                    <>
                                        {translate("competitive.tracker.auto.complete.segments", {
                                            amount: showAccountSegments ? segmentsTabList.length : mySegments.length,
                                        })}
                                        {isSegmentsLocked ? (
                                            <Icon
                                                iconName="locked"
                                                color={colorsPalettes.green.s100}
                                            />
                                        ) : null}
                                    </>
                                )}
                            </CustomTab>
                            {showLinesOfBusiness && isLinesOfBusinessSupported ? (
                                <CustomTab
                                    tooltipText={disableSegments ? disabledTabText : null}
                                    disabled={disableSegments}
                                >
                                    <FlexRow gap={"4px"}>
                                        {translate("segments.querybar.tab.lobs.title")}
                                        <Badge type="new" />
                                    </FlexRow>
                                </CustomTab>
                            ) : null}
                        </CustomTabList>
                    ) : null}
                    <TabPanel>
                        <ScrollAreaWrap style={SCROLL_STYLE}>
                            <Section
                                header={
                                    showCustomTabsList
                                        ? translate(
                                              hasQuery
                                                  ? "common.suggested.websites"
                                                  : selectedMainAsset && websiteResults.length
                                                    ? "autocomplete.websitesCompare.seperator.text.similarSites"
                                                    : "home.page.recents",
                                          )
                                        : null
                                }
                                content={websitesTabList.map(createListItem)}
                            />
                        </ScrollAreaWrap>
                        {isWebsitesMultiSelect ? (
                            <ApplySelection
                                selectedItemsView={selectedItemsView}
                                onApply={onApplyMultiSelect}
                            />
                        ) : null}
                    </TabPanel>
                    <TabPanel>
                        {hasSegments ? (
                            <ScrollAreaWrap style={SCROLL_STYLE}>
                                <Section
                                    content={mySegments.slice(0, DROPDOWN_AMOUNT_TO_DISPLAY).map(createListItem)}
                                    header={
                                        showCustomTabsList
                                            ? (CustomSegmentsSectionHeader ??
                                              translate("common.segments.my", {
                                                  amount: mySegments.length,
                                              }))
                                            : null
                                    }
                                />
                                {showAccountSegments ? (
                                    <Section
                                        content={accountSegments
                                            .slice(0, DROPDOWN_AMOUNT_TO_DISPLAY)
                                            .map(createListItem)}
                                        header={
                                            showCustomTabsList
                                                ? translate("common.segments.account", {
                                                      amount: accountSegments.length,
                                                  })
                                                : null
                                        }
                                    />
                                ) : null}
                            </ScrollAreaWrap>
                        ) : (
                            <NoSegments onClick={onBuildSegmentClick} />
                        )}
                        {isSegmentsMultiSelect ? (
                            <ApplySelection
                                selectedItemsView={selectedItemsView}
                                onApply={onApplyMultiSelect}
                            />
                        ) : null}
                    </TabPanel>
                    <TabPanel>
                        <LobListItems
                            selectedItemsView={selectedItemsView}
                            onClick={handleLobItemClick}
                            query={effectiveLobQuery}
                            applySelectionComponent={
                                isPredefinedMultiSelect ? (
                                    <ApplySelection
                                        selectedItemsView={selectedItemsView}
                                        onApply={onApplyMultiSelect}
                                    />
                                ) : null
                            }
                            excludeItems={getPredefinedExcludedItems()}
                            excludedDomains={getExcludedDomains()}
                            selectionTypeProps={selectionTypeProps}
                            isDisabledListItemSupported={isDisabledListItemSupported}
                        />
                    </TabPanel>
                </TabsView>
            ) : null}
        </div>
    );
};
